import React from "react";
import useMeasure from "@/hooks/useMeasure";
import {useSpring, animated} from "react-spring";
import "@library/fontawesome";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import "./styles";

type Props = {
  header?: any;
  children?: any;
  collapsed?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  maxWidth?: number;
  minHeight?: number;
};

function Section({
  header,
  children,
  collapsed,
  expanded,
  onToggle,
  maxWidth,
  minHeight,
}: Props) {
  const [bind, d] = useMeasure();

  const sp = useSpring({
    maxHeight: `${d.height}px`,
    minHeight: `${d.height + (minHeight || 60)}px`,
    transition: "max-height 50ms linear",
    maxWidth: `${maxWidth}`,
  });

  const [selfExpanded, setSelfExpanded] = React.useState(!collapsed);

  // An owner that passes `expanded` drives the section, so it can be opened from
  // outside — expand all, or jumping to a section from the nav
  const controlled = expanded !== undefined;
  const expand = controlled ? expanded : selfExpanded;

  const toggle = () => {
    if (controlled) {
      if (onToggle) onToggle();
    } else {
      setSelfExpanded((s) => !s);
    }
  };

  // Callers pass headings as their header, which a <button> may not contain, so the
  // header carries button semantics instead of being one
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggle();
    }
  };

  return (
    <animated.div style={sp} className="editor__expandable-section">
      <div
        className="editor__expandable-section__header"
        role="button"
        tabIndex={0}
        aria-expanded={expand}
        onClick={toggle}
        onKeyDown={onKeyDown}
      >
        {header}
        <div
          data-cy="section-control"
          className="editor__expandable-section__control"
        >
          <FontAwesomeIcon icon={expand ? "chevron-up" : "chevron-down"} />
        </div>
      </div>
      <animated.div {...bind} className="editor__expandable-section__content">
        {expand ? children : null}
      </animated.div>
    </animated.div>
  );
}

export default Section;
